import React, { useState, useEffect } from 'react';
import './DailyInflow.css';

const DailyInflow = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDailyInflowData();
  }, []);

  const fetchDailyInflowData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://server.account.majestyfx.com/admin_daily_deposit_withdraw_netdeposit.php');
      
      if (!response.ok) {
        throw new Error('Failed to fetch daily inflow data');
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="daily-inflow-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading daily inflow data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="daily-inflow-container">
        <div className="error-message">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={fetchDailyInflowData} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="daily-inflow-container">
      <div className="page-header">
        <div className="breadcrumb">
          <span>Dashboard</span>
          <span className="separator">/</span>
          <span className="current">Daily Inflow</span>
        </div>
        <h1 className="page-title">Daily Inflow Report</h1>
        <p className="page-description">
          Monitor daily deposits, withdrawals, and net deposit flow
        </p>
      </div>

      <div className="content-wrapper">
        {/* Period Information */}
        <div className="period-card">
          <h3>Report Period</h3>
          <div className="period-info">
            <div className="period-item">
              <span className="label">From:</span>
              <span className="value">{data?.period?.from ? formatDate(data.period.from) : 'N/A'}</span>
            </div>
            <div className="period-item">
              <span className="label">To:</span>
              <span className="value">{data?.period?.to ? formatDate(data.period.to) : 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card deposits">
            <div className="card-icon">
              <i className="fas fa-arrow-down"></i>
            </div>
            <div className="card-content">
              <h3>Total Deposits</h3>
              <p className="amount">{formatCurrency(data?.totals?.total_deposits)}</p>
              <span className="card-label">Incoming Funds</span>
            </div>
          </div>

          <div className="summary-card withdrawals">
            <div className="card-icon">
              <i className="fas fa-arrow-up"></i>
            </div>
            <div className="card-content">
              <h3>Total Withdrawals</h3>
              <p className="amount">{formatCurrency(data?.totals?.total_withdrawals)}</p>
              <span className="card-label">Outgoing Funds</span>
            </div>
          </div>

          <div className={`summary-card net-deposit ${data?.totals?.net_deposit >= 0 ? 'positive' : 'negative'}`}>
            <div className="card-icon">
              <i className={`fas ${data?.totals?.net_deposit >= 0 ? 'fa-chart-line' : 'fa-chart-line-down'}`}></i>
            </div>
            <div className="card-content">
              <h3>Net Deposit</h3>
              <p className="amount">{formatCurrency(data?.totals?.net_deposit)}</p>
              <span className="card-label">
                {data?.totals?.net_deposit >= 0 ? 'Net Inflow' : 'Net Outflow'}
              </span>
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <div className="actions">
          <button onClick={fetchDailyInflowData} className="refresh-btn">
            <i className="fas fa-sync-alt"></i>
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyInflow;
